export const SET_MIN_FILL_RANGE = "appoptics/infrastructure/SET_MIN_FILL_RANGE";
export const SET_MAX_FILL_RANGE = "appoptics/infrastructure/SET_MAX_FILL_RANGE";
export const SET_CUSTOM_THRESHOLDS =
  "appoptics/infrastructure/SET_CUSTOM_THRESHOLDS";
export const FETCH_PENDING_CUSTOM_THRESHOLDS =
  "appoptics/infrastructure/FETCH_PENDING_CUSTOM_THRESHOLDS";
export const CREATE_PENDING_CUSTOM_THRESHOLD =
  "appoptics/infrastructure/CREATE_PENDING_CUSTOM_THRESHOLD";
export const UPDATE_PENDING_CUSTOM_THRESHOLD =
  "appoptics/infrastructure/UPDATE_PENDING_CUSTOM_THRESHOLD";
export const DELETE_PENDING_CUSTOM_THRESHOLD =
  "appoptics/infrastructure/DELETE_PENDING_CUSTOM_THRESHOLD";
export const CLEAR_PENDING_CUSTOM_THRESHOLDS =
  "appoptics/infrastructure/CLEAR_PENDING_CUSTOM_THRESHOLDS";
export const CLEAR_CUSTOM_THRESHOLDS =
  "appoptics/infrastructure/CLEAR_CUSTOM_THRESHOLDS";

/*
  {
    customThresholds: [
      { value: 20, color: "#6DB359", },
      { value: 50, color: "#FAC519", },
    ],
    pendingCustomThresholds: [
      { id: 75, value: 75, color: "#F28D39", isInvalid: false },
      { id: 90, value: 900, color: "#EA4C3B", isInvalid: true },
    ]
  }
*/

export const INITIAL_STATE = {
  minFillRange: 0,
  maxFillRange: 100,
  customThresholds: [],
  pendingCustomThresholds: [],
};

export const setMinFillRange = range => ({
  type: SET_MIN_FILL_RANGE,
  minFillRange: range,
});

export const setMaxFillRange = range => ({
  type: SET_MAX_FILL_RANGE,
  maxFillRange: range,
});

export const setCustomThresholds = (thresholds = []) => ({
  type: SET_CUSTOM_THRESHOLDS,
  thresholds: thresholds,
});

export const fetchPendingCustomThresholds = () => ({
  type: FETCH_PENDING_CUSTOM_THRESHOLDS,
});

export const createPendingCustomThreshold = (value, color) => ({
  type: CREATE_PENDING_CUSTOM_THRESHOLD,
  threshold: {
    id: parseFloat(value),
    value: parseFloat(value),
    color: color,
    isInvalid: false,
  },
});

export const updatePendingCustomThreshold = (id, value, color, isInvalid) => ({
  type: UPDATE_PENDING_CUSTOM_THRESHOLD,
  threshold: {
    id: parseFloat(id),
    value: parseFloat(value),
    color: color,
    isInvalid: isInvalid,
  },
});

export const deletePendingCustomThreshold = id => ({
  type: DELETE_PENDING_CUSTOM_THRESHOLD,
  id: id,
});

export const clearPendingCustomThresholds = () => ({
  type: CLEAR_PENDING_CUSTOM_THRESHOLDS,
});

export const clearCustomThresholds = () => ({
  type: CLEAR_CUSTOM_THRESHOLDS,
});

export const sortPendingCustomThresholds = (state, action) => {
  let pendingCustomThresholds = [
    ...state.pendingCustomThresholds,
    action.threshold,
  ];

  return pendingCustomThresholds.sort((a, b) => a.value - b.value);
};

export const replacePendingCustomThreshold = (state, action) => {
  const { pendingCustomThresholds } = state;
  const { id, value, color, isInvalid } = action.threshold;

  const thresholdIndex = pendingCustomThresholds.findIndex(t => t.id == id);

  const existingThreshold = pendingCustomThresholds[thresholdIndex];

  const newThreshold = {
    id: id ? id : value,
    value: value ? value : existingThreshold.value,
    color: color ? color : existingThreshold.color,
    isInvalid: isInvalid,
  };
  pendingCustomThresholds[thresholdIndex] = newThreshold;

  return pendingCustomThresholds;
};

const removeThresholdsAboveMax = (thresholds = [], max) =>
  thresholds.filter(threshold => threshold.value <= max);

const removeThresholdsBelowMin = (thresholds = [], min) =>
  thresholds
    .map((threshold, index) => {
      const nextThreshold = thresholds[index + 1];
      if (nextThreshold && nextThreshold.value <= min) return null;
      if (threshold.value < min) return { ...threshold, value: min };
      return threshold;
    })
    .filter(threshold => threshold);

const applyNewMaxFillRange = (state, maxFillRange) => {
  const { customThresholds: current, pendingCustomThresholds: pending } = state;
  return {
    maxFillRange,
    customThresholds: removeThresholdsAboveMax(current, maxFillRange),
    pendingCustomThresholds: removeThresholdsAboveMax(pending, maxFillRange),
  };
};
const applyNewMinFillRange = (state, minFillRange) => {
  const { customThresholds: current, pendingCustomThresholds: pending } = state;
  return {
    minFillRange,
    customThresholds: removeThresholdsBelowMin(current, minFillRange),
    pendingCustomThresholds: removeThresholdsBelowMin(pending, minFillRange),
  };
};

export const convertToPendingCustomThresholds = (thresholds = []) => {
  const convertedThresholds = thresholds.map(threshold => {
    return {
      ...threshold,
      id: threshold.value,
      isInvalid: false,
    };
  });

  return convertedThresholds;
};

export const convertFromPendingCustomThresholds = (thresholds = []) => {
  const convertedThresholds = thresholds.map(threshold => {
    return {
      value: threshold.value,
      color: threshold.color,
    };
  });

  return convertedThresholds;
};

const customThresholdsReducer = (state = INITIAL_STATE, action = {}) => {
  switch (action.type) {
    case SET_MIN_FILL_RANGE:
      return {
        ...state,
        ...applyNewMinFillRange(state, action.minFillRange),
      };
    case SET_MAX_FILL_RANGE:
      return {
        ...state,
        ...applyNewMaxFillRange(state, action.maxFillRange),
      };
    case SET_CUSTOM_THRESHOLDS:
      return {
        ...state,
        customThresholds: convertFromPendingCustomThresholds(action.thresholds),
      };
    case FETCH_PENDING_CUSTOM_THRESHOLDS:
      return {
        ...state,
        pendingCustomThresholds: convertToPendingCustomThresholds(
          state.customThresholds
        ),
      };
    case CREATE_PENDING_CUSTOM_THRESHOLD:
      return {
        ...state,
        pendingCustomThresholds: sortPendingCustomThresholds(state, action),
      };
    case UPDATE_PENDING_CUSTOM_THRESHOLD:
      return {
        ...state,
        pendingCustomThresholds: replacePendingCustomThreshold(state, action),
      };
    case DELETE_PENDING_CUSTOM_THRESHOLD:
      return {
        ...state,
        pendingCustomThresholds: [
          ...state.pendingCustomThresholds.filter(
            threshold => threshold.id !== action.id
          ),
        ],
      };
    case CLEAR_PENDING_CUSTOM_THRESHOLDS:
      return {
        ...state,
        pendingCustomThresholds: [],
      };
    case CLEAR_CUSTOM_THRESHOLDS:
      return {
        ...INITIAL_STATE,
      };
    default:
      return state;
  }
};

export default customThresholdsReducer;
